import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,

  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  IconButton,
  Fade,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  Folder as FolderIcon,
  Link as LinkIcon,
  Autorenew as AutorenewIcon,
  Map as MapIcon,
  Terminal as TerminalIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  BugReport as BugReportIcon,
  People as PeopleIcon,
  Help as HelpIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CardMembership as CardMembershipIcon,
} from '@mui/icons-material';
import Navbar from '../mainPage/components/Navbar';

const Documentation = () => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState('panel1');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Ensure page always starts at the top when visiting Documentation
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const accordionStyle = {
    mb: 2,
    borderRadius: '12px !important',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${theme.palette.divider}`,
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
    },
  };

  const accordionSummaryStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    minHeight: '72px',
    '&.Mui-expanded': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: 2,
    },
  };

  const accordionDetailsStyle = {
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '24px',
  };

  const questionStyle = {
    fontWeight: 600,
    color: theme.palette.primary.main,
    mb: 2,
    fontSize: '1.1rem',
  };

  const answerStyle = {
    color: theme.palette.text.primary,
    lineHeight: 1.75,
    mb: 2,
    fontSize: '0.95rem',
  };

  const listItemStyle = {
    py: 0.5,
    pl: 2,
  };

  const iconStyle = {
    color: theme.palette.primary.main,
    fontSize: '2rem',
  };




  // Documentation sections data
  const documentationSections = [
    {
      id: 'panel1',
      title: 'Login & Access',
      icon: <SecurityIcon sx={iconStyle} />,
      questions: [
        {
          question: 'How do I log in to the ETL Integration Web App?',
          answer: 'To log in, visit https://etl-frontend-zeta.vercel.app. You can log in using:',
          list: [
            'A username and password, or',
            'A Google account.'
          ],
          additionalInfo: 'Once logged in, you\'ll be redirected to your dashboard where you can view and manage projects.'
        }
      ]
    },
    {
      id: 'panel2',
      title: 'Dashboard',
      icon: <DashboardIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What information is shown on the Dashboard?',
          answer: 'The Dashboard provides a quick overview of system activity. You can see:',
          list: [
            'Active projects',
            'Running workflows',
            'Error logs',
            'Health indicators of your ETL system',
            'Execution history for scheduled or completed flows'
          ]
        }
      ]
    },
    {
      id: 'panel3',
      title: 'Projects',
      icon: <FolderIcon sx={iconStyle} />,
      questions: [
        {
          question: 'How do I create a project?',
          answer: 'To create a project:',
          list: [
            'Navigate to Create Projects.',
            'Enter the project name, description, and ERP systems involved.',
            'Save your project.'
          ],
          additionalInfo: 'This will set the foundation for creating workflows and connections.'
        }
      ]
    },
    {
      id: 'panel4',
      title: 'Connections',
      icon: <LinkIcon sx={iconStyle} />,
      questions: [
        {
          question: 'How do I set up API connections?',
          answer: 'To connect your ERP systems or other APIs:',
          list: [
            'Navigate to Create Connections.',
            'Enter source and destination API details (URL, authentication, and headers).',
            'Test the connection to ensure it works.'
          ],
          additionalInfo: 'Saved connections can be reused across multiple workflows.'
        }
      ]
    },
    {
      id: 'panel5',
      title: 'Workflows',
      icon: <AutorenewIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What is a workflow and how do I create one?',
          answer: 'A workflow defines the full path of data movement from extraction to loading. To create one:',
          list: [
            'Go to Create Workflow.',
            'Choose a project, source, and destination.',
            'Map fields between source and destination, applying conditions and functions as needed.',
            'Define execution time (schedule or run immediately).',
            'Save and enable the workflow.'
          ]
        }
      ]
    },
    {
      id: 'panel6',
      title: 'Field Mapping',
      icon: <MapIcon sx={iconStyle} />,
      questions: [
        {
          question: 'How do I map fields between source and destination?',
          answer: '',
          list: [
            'Select your workflow → Click Map Fields.',
            'Use drag-and-drop or manual mapping.',
            'Apply transformation rules such as trimming spaces, concatenating strings, or formatting dates.'
          ],
          additionalInfo: 'The system also provides fuzzy logic to auto-match fields with ~80% accuracy, based on names or relevant meanings.'
        }
      ]
    },
    {
      id: 'panel7',
      title: 'Console',
      icon: <TerminalIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What does the Console show?',
          answer: 'The Console provides real-time logs of ETL processes, including:',
          list: [
            'Extracted data',
            'Transformed data',
            'Loaded data',
            'Status of each job execution'
          ],
          additionalInfo: 'This is where you can monitor workflow health and troubleshoot issues.'
        }
      ]
    },
    {
      id: 'panel8',
      title: 'Notifications',
      icon: <NotificationsIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What notifications does the system provide?',
          answer: 'The system sends alerts for:',
          list: [
            'API errors',
            'Field mismatches'
          ],
          additionalInfo: 'Alerts appear inside the app, ensuring you\'re immediately aware of issues.'
        }
      ]
    },
    {
      id: 'panel9',
      title: 'Reports',
      icon: <AssessmentIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What reporting features are available?',
          answer: 'The system offers both prebuilt reports and custom reports with:',
          list: [
            'Export options',
            'Filtering capabilities',
            'Weekly and monthly consolidated views of flow counts and statuses'
          ]
        }
      ]
    },
    {
      id: 'panel10',
      title: 'Jira Integration',
      icon: <BugReportIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What happens if an API call fails?',
          answer: 'If an API call fails, the system:',
          list: [
            'Automatically creates a Jira ticket (if Jira is configured).',
            'Logs the failure in the Console for review.',
            'Sends an alert for visibility.'
          ],
          additionalInfo: 'If Jira is not configured, the ticket will not be raised, but Console logs and alerts will still show the failure.'
        }
      ]
    },
    {
      id: 'panel11',
      title: 'User Roles',
      icon: <PeopleIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What roles are available in the app and what can they do?',
          answer: '',
          roles: [
            { label: 'Admin', description: 'Full access to all modules, user management, project/workflow creation.', color: 'primary' },
            { label: 'User', description: 'Limited to creating and executing projects and workflows.', color: 'secondary' }
          ]
        }
      ]
    },
    {
      id: 'panel13',
      title: 'Trial & Subscription',
      icon: <CardMembershipIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What is the free trial?',
          answer: 'New users and organizations receive a 14-day free trial period to explore the platform. During the trial, you have access to all features with some limits:',
          list: [
            'Maximum 5 active connectors (configurable by contact support)',
            'Full access to create projects, workflows, and connections',
            'All transformation and mapping features available'
          ],
          additionalInfo: 'The trial period starts from when your account or organization is created.'
        },
        {
          question: 'How long is my trial period?',
          answer: 'The default trial period is 14 days from when your account or organization is created. You can check your remaining trial days:',
          list: [
            'On the dashboard (trial warning banner if 1-4 days remaining)',
            'In your account settings',
            'Via email notifications when trial is ending'
          ]
        },
        {
          question: 'What happens when my trial expires?',
          answer: 'When your trial period ends:',
          list: [
            'Your account becomes inactive',
            'You will not be able to log in',
            'All workflows and connections are preserved',
            'Contact support to upgrade to a paid subscription',
            'Contact support can reactivate your trial if needed'
          ],
          note: 'Your data is never deleted. Once you upgrade or reactivate, everything will be available again.'
        },
        {
          question: 'How do I upgrade to a paid subscription?',
          answer: 'To upgrade to a paid subscription:',
          list: [
            'Contact your organization administrator',
            'Or contact support for individual accounts',
            'Subscription periods available: 1, 3, 6, 12 months, or custom days',
            'Once upgraded, all users in your organization are automatically activated'
          ]
        },
        {
          question: 'What are subscription periods?',
          answer: 'Paid subscriptions are available for:',
          list: [
            '1 month',
            '3 months',
            '6 months',
            '12 months',
            'Custom period (1-365 days)'
          ],
          additionalInfo: 'Your subscription end date is calculated from the start date plus the selected period.'
        },
        {
          question: 'What happens when my subscription expires?',
          answer: 'When your subscription period ends:',
          list: [
            'Your account automatically becomes inactive',
            'You will not be able to log in',
            'All your data (projects, workflows, connections) is preserved',
            'Contact support to renew your subscription',
            'Contact support can reactivate your account or extend your subscription'
          ],
          note: 'All your work is safe and will be available again once you renew.'
        },
        {
          question: 'How do I check my trial or subscription status?',
          answer: 'You can check your account status in several ways:',
          list: [
            'Dashboard: Look for trial/subscription status indicators',
            'Trial warning banner: Appears when 1-4 days remaining',
            'Email notifications: Sent when trial/subscription is ending',
            'Login response: Shows trial status and days remaining',
            'Contact support: For detailed account information'
          ]
        },
        {
          question: 'What are trial warnings?',
          answer: 'The system sends warnings when your trial is ending soon:',
          list: [
            'Warning period: 1-4 days before expiration',
            'Warning banner: Shown on dashboard',
            'Email notifications: Sent to you and administrators',
            'Purpose: Remind you to upgrade before access is restricted'
          ]
        },
        {
          question: 'Can my trial be extended?',
          answer: 'Yes, trial periods can be extended by SuperAdmin:',
          list: [
            'Contact support to request an extension',
            'Contact support can extend by additional days',
            'Or set a new trial end date',
            'Extensions are typically granted for evaluation purposes'
          ]
        },
        {
          question: 'What if my account is inactive?',
          answer: 'If your account is inactive, you cannot:',
          list: [
            'Log in to the system',
            'Access your projects or workflows',
            'Create new connections',
            'Execute workflows'
          ],
          additionalInfo: 'To reactivate: Contact support or your organization administrator, upgrade to a paid subscription, or request trial extension (if applicable).'
        },
        {
          question: 'What is the difference between trial and subscription?',
          answer: '',
          list: [
            'Trial: Free 14-day period for new users/organizations, limited to 5 active connectors, full feature access during trial period, automatically expires after 14 days',
            'Subscription (Paid): Paid access with selected period (1-12 months or custom), no connector limits, full feature access, automatically expires after subscription period, can be renewed or extended'
          ]
        },
        {
          question: 'Who manages subscriptions and trials?',
          answer: 'Subscription and trial management:',
          list: [
            'Contact support: Can create organizations, set subscription periods, extend trials, and manage account status',
            'Organization Admin: Can view organization status but cannot change subscription settings',
            'Individual Users: Can view their own trial status but cannot modify it',
            'Support Team: Can assist with upgrades, renewals, and account reactivation'
          ]
        }
      ]
    },
    {
      id: 'panel12',
      title: 'Common FAQs',
      icon: <HelpIcon sx={iconStyle} />,
      questions: [
        {
          question: 'What is ETL?',
          answer: 'ETL stands for Extract, Transform, Load. It is the process of moving data from a source system, applying transformations or cleanup, and loading it into a destination system.'
        },
        {
          question: 'Can I use this app with any ERP?',
          answer: 'Yes, as long as the ERP supports APIs (REST is recommended).'
        },
        {
          question: 'Can I create workflows with multiple sources and destinations?',
          answer: 'Yes, multiple sources and destinations are supported.'
        },
        {
          question: 'Can I schedule my workflows?',
          answer: 'Yes, workflows can be scheduled at a defined time. Scheduling follows the UTC time zone.'
        },
        {
          question: 'Can I restrict or filter the data being transferred?',
          answer: 'Yes, you can apply filters on fields to limit the data sent from source to destination.'
        },
        {
          question: 'Where can I see failures in my workflow?',
          answer: 'Failures are displayed at the bottom of the workflow execution screen. You can click the "eye" icon to view detailed logs for a failed execution.'
        },
        {
          question: 'Can I see my flow execution history later?',
          answer: 'Yes, the Dashboard displays execution history. You can click a workflow ID to see all execution details.'
        },
        {
          question: 'Can I cancel or delete a workflow?',
          answer: '',
          list: [
            'On-demand flows: You can cancel during execution.',
            'Scheduled flows: You can update the date/time or delete the flow entirely.'
          ],
          note: 'Deleted workflows and mappings cannot be recovered.'
        },
        {
          question: 'What transformation options are available?',
          answer: 'You can apply transformations to:',
          list: [
            'Strings (trim, concat, case conversion)',
            'Numbers (rounding, arithmetic)',
            'Dates (formatting, parsing)',
            'Utility functions (lookup, reference external fields, fuzzy matching)'
          ]
        },
        {
          question: 'Can I save my connections for reuse?',
          answer: 'Yes, saved connections are visible under the Connections screen and can be reused for new workflows.'
        },
        {
          question: 'Is this application only for ERP systems?',
          answer: 'Primarily yes, but it also integrates with Jira (for ticket logging) and SQL Server (for on-premise backup/dump).'
        },
        {
          question: 'Does the app provide AI assistance?',
          answer: 'Yes, the app includes AI-powered fuzzy logic for automatic field mapping and intelligent suggestions for data transformations.'
        }
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: "#3044A9" }}>
      <Navbar
        hideLoginButton={false}
        enableScrollEffect={false}
        customStyles={{
          appBar: {
            backgroundColor: "#3044A9",
            backdropFilter: 'none',
          },
          toolbar: {
            background: 'transparent',
          },
        }}
      />

      <Container maxWidth="lg" sx={{ pt: 14, pb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{
            fontWeight: 700,
            color: "#fff",
            my: 2
          }}>
            ETL Integration Documentation
          </Typography>
          <Typography variant="subtitle1" sx={{
            color: "#fff",
            maxWidth: 700,
            mx: 'auto',
            my: 2
          }}>
            Comprehensive guide to using the ETL Integration Web Application
          </Typography>
        </Box>




        {/* Accordion Sections */}
        <Box sx={{ mb: 4 }}>
          {documentationSections.map((section, index) => (
            <Fade in timeout={1000 + (index * 100)} key={section.id}>
              <Accordion
                expanded={expanded === section.id}
                onChange={handleAccordionChange(section.id)}
                sx={accordionStyle}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                  sx={accordionSummaryStyle}
                >
                  {section.icon}
                  <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: '1.3rem',
                  }}>
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsStyle}>
                  {section.questions.map((qa, qaIndex) => (
                    <Card key={qaIndex} sx={{
                      mb: qaIndex < section.questions.length - 1 ? 3 : 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      borderRadius: 2,
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={questionStyle}>
                          {qa.question}
                        </Typography>

                        {qa.answer && (
                          <Typography sx={answerStyle}>
                            {qa.answer}
                          </Typography>
                        )}

                        {qa.list && (
                          <List dense sx={{ mt: 1, mb: 2 }}>
                            {qa.list.map((item, itemIndex) => (
                              <ListItem key={itemIndex} sx={listItemStyle}>
                                <ListItemText
                                  primary={item}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.95rem',
                                      color: theme.palette.text.primary,
                                    }
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}

                        {qa.roles && (
                          <Box sx={{ mt: 2 }}>
                            {qa.roles.map((role, roleIndex) => (
                              <Box key={roleIndex} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 1.5,
                                p: 2,
                                backgroundColor: theme.palette.background.default,
                                borderRadius: 1,
                              }}>
                                <Chip
                                  label={role.label}
                                  color={role.color}
                                  size="medium"
                                  sx={{ fontWeight: 600 }}
                                />
                                <Typography sx={{ fontSize: '0.95rem' }}>
                                  {role.description}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {qa.additionalInfo && (
                          <Typography sx={{
                            ...answerStyle,
                            backgroundColor: theme.palette.background.default,
                            p: 2,
                            borderRadius: 1,
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                            mt: 2,
                          }}>
                            {qa.additionalInfo}
                          </Typography>
                        )}

                        {qa.note && (
                          <Typography sx={{
                            ...answerStyle,
                            backgroundColor: theme.palette.warning.light,
                            color: theme.palette.warning.contrastText,
                            p: 2,
                            borderRadius: 1,
                            mt: 2,
                            fontWeight: 600,
                          }}>
                            <strong>Note:</strong> {qa.note}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Fade>
          ))}
        </Box>

   
      </Container>

      {/* Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      </Fade>
    </Box>
  );
};

export default Documentation;